import createRequest from "../utils/create-request";

interface ApplePayUrls {
    validateMerchant: string;
    success: string;
    cleanup: string;
}

interface ApplePayCallbacks {
    payment?: (config: ApplePayConfig) => void;
    paymentAuthorized?: {
        start?: (config: ApplePayConfig) => void,
        fail?: (data, config: ApplePayConfig) => void,
        success?: (data, config: ApplePayConfig) => void,
        error?: (reason, config: ApplePayConfig) => void
    };
    cancel?: (data, config: ApplePayConfig) => void
}

export interface ApplePayConfig {
    urls: ApplePayUrls;
    callbacks?: ApplePayCallbacks;
    merchantIdentifier: string;
    allowedLanguages: string[];
    paymentsOptions: ApplePayJS.ApplePayPaymentRequest
}

export class ApplePay {
    private readonly config: ApplePayConfig
    private session?: ApplePaySession
    private data?: {
        fundID: number,
        price: number,
        sourceCode: number,
    }

    constructor(config: ApplePayConfig) {
        this.config = config;
        ApplePay.log("Init", this.config)
    }

    public canMakePayments(): boolean {
        let canMakePayments = !!window["ApplePaySession"] && ApplePaySession.canMakePayments()

        if (!canMakePayments) {
            ApplePay.log("Not capable of making payments")
            return false
        }

        ApplePay.log("Capable of making payments")
        return canMakePayments
    }

    public getAllowedLanguages(): string[] {
        return this.config.allowedLanguages;
    }

    public async canMakePaymentsWithActiveCard(): Promise<boolean> {
        return await ApplePaySession
            .canMakePaymentsWithActiveCard(this.config.merchantIdentifier)
            .then((canMakePaymentsWithActiveCard) => {
                if (canMakePaymentsWithActiveCard) {
                    ApplePay.log("Capable of making payments with active card")
                } else {
                    ApplePay.log("Not capable of making payments with active card")
                }

                return canMakePaymentsWithActiveCard;
            })
    }

    public setData(data) {
        this.data = data;
    }

    public startPayment() {
        this.config.callbacks && this.config.callbacks.payment && this.config.callbacks.payment(this.config)

        let result: ApplePayJS.ApplePayPaymentRequest = this.config.paymentsOptions
        result.total.amount = this.data.price.toString()
        result.lineItems[0].amount = this.data.price.toString()

        ApplePay.log("Start payment")

        this.session = new ApplePaySession(1, result)
        this.session.onvalidatemerchant = this.onValidateMerchant.bind(this)
        this.session.onpaymentauthorized = this.onPaymentAuthorized.bind(this)
        this.session.oncancel = this.onCancel.bind(this)
        this.session.begin()
    }

    private onCancel(event: ApplePayJS.Event): void {
        ApplePay.log("Payment: Cancel")
        this.config.callbacks && this.config.callbacks.cancel && this.config.callbacks.cancel({}, this.config)
    }

    private onValidateMerchant(event: ApplePayJS.ApplePayValidateMerchantEvent): void {
        ApplePay.log("Validate merchant")
        createRequest(this.config.urls.validateMerchant, {
            url: event.validationURL
        }).then((response) => {
            ApplePay.log("Validate merchant: Success", response)
            this.session.completeMerchantValidation(response)
        })
        .catch((reason) => {
            ApplePay.log("Validate merchant: Error", reason)
        })
    }

    private onPaymentAuthorized(event: ApplePayJS.ApplePayPaymentAuthorizedEvent): void {
        this.config.callbacks && this.config.callbacks.paymentAuthorized && this.config.callbacks.paymentAuthorized.start && this.config.callbacks.paymentAuthorized.start(this.config);

        ApplePay.log("Payment Authorized")

        // @ts-ignore
        event.payment.data = this.data;
        createRequest(this.config.urls.success, event.payment)
            .then((data: {
                status: string,
                redirect_url: string
            }) => {
                if (typeof data.status !== 'undefined' && data.status == 'success') {
                    ApplePay.log("Payment Authorized: Success")
                    this.session.completePayment(ApplePaySession.STATUS_SUCCESS)
                    this.config.callbacks && this.config.callbacks.paymentAuthorized && this.config.callbacks.paymentAuthorized.success && this.config.callbacks.paymentAuthorized.success(data, this.config)
                } else {
                    ApplePay.log("Payment Authorized: Fail")
                    this.session.completePayment(ApplePaySession.STATUS_FAILURE)
                    this.config.callbacks && this.config.callbacks.paymentAuthorized && this.config.callbacks.paymentAuthorized.fail && this.config.callbacks.paymentAuthorized.fail(data, this.config)
                }
            })
            .catch((reason) => {
                ApplePay.log("Payment Authorized: Error", reason)
                this.config.callbacks && this.config.callbacks.paymentAuthorized && this.config.callbacks.paymentAuthorized.error && this.config.callbacks.paymentAuthorized.error(reason, this.config)
            })
    }

    private static log(message?: string, ...optionalParams: any[]): void {
        console.log(`ApplePay: ${message}`, ...optionalParams)
    }
}



// WEBPACK FOOTER //
// ./@modules/applepay/apple-pay.ts